/**
 * Per-board view preferences, stored on the device.
 *
 * Scoped by board for the same reason the filter preferences are: a calendar
 * overlay that helps on a board with a connected calendar is noise on one
 * without, and the view you work a project board in is rarely the view you
 * work a support board in.
 *
 * Every read and write is wrapped. A private window, blocked site data or a
 * full quota must cost the stored preference, never the page.
 */

const keyFor = (boardId: string, name: string): string =>
  `board.${boardId}.${name}`;

export function readBoardPreference(
  boardId: string | undefined,
  name: string,
): string | null {
  if (!boardId || typeof window === "undefined") return null;

  try {
    return window.localStorage.getItem(keyFor(boardId, name));
  } catch {
    return null;
  }
}

export function writeBoardPreference(
  boardId: string | undefined,
  name: string,
  value: string,
): void {
  if (!boardId || typeof window === "undefined") return;

  try {
    window.localStorage.setItem(keyFor(boardId, name), value);
  } catch {
    // Nothing to do and nothing worth telling the user: the preference simply
    // will not be there next time.
  }
}

/**
 * A stored list of ids. Anything that is not a JSON array of strings — a
 * hand-edited value, an older format, a truncated write — reads as an empty
 * list rather than throwing, and stray non-string entries are dropped.
 */
export function readBoardPreferenceList(
  boardId: string | undefined,
  name: string,
): string[] {
  const raw = readBoardPreference(boardId, name);
  if (!raw) return [];

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return Array.from(
      new Set(
        parsed.filter(
          (value): value is string =>
            typeof value === "string" && value.length > 0,
        ),
      ),
    );
  } catch {
    return [];
  }
}

export function writeBoardPreferenceList(
  boardId: string | undefined,
  name: string,
  values: readonly string[],
): void {
  writeBoardPreference(boardId, name, JSON.stringify(values));
}
